import '../icon-button';
import '../icon';
import '../stop-events';
import '../title';
import { defineCustomElement, getAttribute, getBooleanAttribute, getRect, isAttrTrue, updateAttribute, getReactEventHandler, NectaryElement, updateBooleanAttribute, setClass, isTargetEqual, isAttrEqual } from '../utils';
const templateHTML = '<style>:host{display:contents;--sinch-comp-dialog-max-width:512px;--sinch-comp-dialog-max-height:90vh;--sinch-comp-dialog-width:fit-content}#dialog{position:fixed;left:0;right:0;margin:auto;display:flex;flex-direction:column;padding:24px 0;width:var(--sinch-comp-dialog-width);max-width:var(--sinch-comp-dialog-max-width);max-height:var(--sinch-comp-dialog-max-height);border-radius:var(--sinch-comp-dialog-shape-radius);box-sizing:border-box;contain:content;background-color:var(--sinch-comp-dialog-color-default-background-initial);border:none;box-shadow:var(--sinch-comp-dialog-shadow);outline:0}#dialog:not([open]){display:none}dialog::backdrop{background-color:#000;opacity:.55}#header{display:flex;flex-direction:row;align-items:flex-start;margin-bottom:12px;padding:0 24px;gap:8px;--sinch-global-size-icon:24px;--sinch-global-color-icon:var(--sinch-comp-dialog-color-default-icon-initial)}#caption{--sinch-global-color-text:var(--sinch-comp-dialog-color-default-title-initial);--sinch-comp-title-font:var(--sinch-comp-dialog-font-title)}#content{min-height:0;overflow:auto;padding:4px 24px}#action{display:flex;flex-direction:row;justify-content:flex-end;gap:16px;margin-top:20px;padding:0 24px}#action.empty{display:none}#close{position:relative;left:4px;top:-4px;margin-left:auto}</style><dialog id="dialog"><div id="header"><slot id="icon" name="icon"></slot><sinch-title id="caption" type="m" level="3"></sinch-title><sinch-icon-button id="close" size="s"><sinch-icon id="icon-close" slot="icon" name="close"></sinch-icon></sinch-icon-button></div><div id="content"><sinch-stop-events events="close"><slot name="content"></slot></sinch-stop-events></div><div id="action"><sinch-stop-events events="close"><slot name="buttons"></slot></sinch-stop-events></div></dialog>';
import { disableScroll, enableScroll } from './utils';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-dialog', class extends NectaryElement {
  #$dialog;
  #$closeButton;
  #$caption;
  #$actionWrapper;
  #$actionSlot;
  #controller = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$dialog = shadowRoot.querySelector('#dialog');
    this.#$closeButton = shadowRoot.querySelector('#close');
    this.#$caption = shadowRoot.querySelector('#caption');
    this.#$actionWrapper = shadowRoot.querySelector('#action');
    this.#$actionSlot = shadowRoot.querySelector('slot[name="buttons"]');
  }
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const options = {
      signal: this.#controller.signal
    };
    this.role = 'dialog';
    this.#$closeButton.addEventListener('click', this.#onCloseClick, options);
    this.#$dialog.addEventListener('mousedown', this.#onBackdropMouseDown, options);
    this.#$dialog.addEventListener('cancel', this.#onCancel, options);
    this.#$actionSlot.addEventListener('slotchange', this.#onActionSlotChange, options);
    this.addEventListener('-close', this.#onCloseReactHandler, options);
    this.#onActionSlotChange();
    if (this.open) {
      this.#onExpand();
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#onCollapse();
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['caption', 'open', 'close-aria-label'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (isAttrEqual(oldVal, newVal)) {
      return;
    }
    switch (name) {
      case 'caption':
        {
          updateAttribute(this.#$caption, 'text', newVal);
          break;
        }
      case 'open':
        {
          const shouldOpen = isAttrTrue(newVal);
          if (shouldOpen) {
            requestAnimationFrame(() => {
              this.#onExpand();
            });
          } else {
            this.#onCollapse();
          }
          updateBooleanAttribute(this, 'open', shouldOpen);
          break;
        }
      case 'close-aria-label':
        {
          updateAttribute(this.#$closeButton, 'aria-label', newVal);
          break;
        }
    }
  }
  set caption(caption) {
    updateAttribute(this, 'caption', caption);
  }
  get caption() {
    return getAttribute(this, 'caption', '');
  }
  set open(isOpen) {
    updateBooleanAttribute(this, 'open', isOpen);
  }
  get open() {
    return getBooleanAttribute(this, 'open');
  }
  get dialogRect() {
    return getRect(this.#$dialog);
  }
  get closeButtonRect() {
    return getRect(this.#$closeButton);
  }
  #onCancel = e => {
    e.preventDefault();
    e.stopPropagation();
    this.#dispatchCloseEvent('escape');
  };
  #onCloseClick = () => {
    this.#dispatchCloseEvent('close');
  };
  #onBackdropMouseDown = e => {
    if (isTargetEqual(e, this.#$dialog)) {
      const rect = this.dialogRect;
      const isInside = e.x >= rect.x && e.x < rect.x + rect.width && e.y >= rect.y && e.y < rect.y + rect.height;
      if (!isInside) {
        e.stopPropagation();
        this.#dispatchCloseEvent('backdrop');
      }
    }
  };
  #onCloseReactHandler = e => {
    getReactEventHandler(this, 'on-close')?.(e);
  };
  #dispatchCloseEvent(detail) {
    this.dispatchEvent(new CustomEvent('-close', {
      detail
    }));
  }
  #onExpand() {
    if (!this.isDomConnected || this.#$dialog.open || !this.open) {
      return;
    }
    this.#$dialog.showModal();
    disableScroll();
  }
  #onCollapse() {
    if (!this.#$dialog.open) {
      return;
    }
    this.#$dialog.close?.();
    enableScroll();
  }
  #onActionSlotChange = () => {
    setClass(this.#$actionWrapper, 'empty', this.#$actionSlot.assignedElements().length === 0);
  };
});